import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
  memo,
} from 'react';
import Card from '@common/components/card';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@common/hooks';
import * as loggedUserSelector from '@modules/core/selectors/logged-user';
import Icon from '@common/components/icon';
import { EvaluationDetailResponse } from '@modules/performance/types';
import { useEvaluationStore } from './store';
import RatingScalesModal from '@modules/performance/components/rating-scales-modal/rating-scales-modal';
import CompetencyHeader from './competency-header';
import CategoryHeader from './category-header';
import CompetencyItem from './competency-item';
import { useCompetencyHandlers } from './hooks/use-competency-handlers';
import { useStickyHeader } from './hooks/use-sticky-header';
import {
  computeCompetencyDisplayValue,
  getSortedScales,
  roundToOneDecimal,
  resolveScaleColorForValue,
} from '@modules/performance/utils/utils';

interface CompetencyContainerReadOnlyProps {
  evaluation: EvaluationDetailResponse['data'];
  isDrawerMode?: boolean;
  drawerResponsesByComponentId?: Record<
    string,
    { answersByUserId: Record<string, any> }
  >;
  drawerFilteredCompetencyGroups?: any[];
  onShowRatingScalesInDrawer?: (scales: any[]) => void;
}

const CompetencyContainerReadOnly = memo<CompetencyContainerReadOnlyProps>(
  ({
    evaluation,
    isDrawerMode = false,
    drawerResponsesByComponentId,
    drawerFilteredCompetencyGroups,
    onShowRatingScalesInDrawer,
  }) => {
    const { t } = useTranslation();
    const {
      filteredCompetencyGroups: storeFilteredCompetencyGroups,
      getSelectedScaleId,
      validationErrorsByComponentId,
      responsesByComponentId: storeResponsesByComponentId,
    } = useEvaluationStore();

    // Use drawer data when in drawer mode, otherwise use store data
    const filteredCompetencyGroups =
      isDrawerMode && drawerFilteredCompetencyGroups
        ? drawerFilteredCompetencyGroups
        : storeFilteredCompetencyGroups;

    const responsesByComponentId =
      isDrawerMode && drawerResponsesByComponentId
        ? drawerResponsesByComponentId
        : storeResponsesByComponentId;

    // Custom getSelectedScaleId for drawer mode
    const getSelectedScaleIdForDrawer = React.useCallback(
      (role: 'reviewee' | 'reviewer', competencyId: string) => {
        if (!isDrawerMode || !drawerResponsesByComponentId) {
          return getSelectedScaleId(role, competencyId);
        }

        const compId = String(competencyId);
        const answersByUserId =
          drawerResponsesByComponentId?.[compId]?.answersByUserId;

        if (answersByUserId) {
          const userId =
            role === 'reviewee'
              ? String(evaluation?.reviewee?.id ?? '')
              : String(evaluation?.reviewers?.[0]?.id ?? '');
          const rawAnswer = userId ? answersByUserId[userId] : undefined;

          if (rawAnswer && typeof rawAnswer === 'object') {
            const nested: any = (rawAnswer as any).answer || rawAnswer;
            const scaleId = nested?.scale_id || nested?.scaleId;
            if (scaleId) return String(scaleId);
          }
        }

        return undefined;
      },
      [
        isDrawerMode,
        drawerResponsesByComponentId,
        getSelectedScaleId,
        evaluation,
      ]
    );

    const effectiveGetSelectedScaleId = isDrawerMode
      ? getSelectedScaleIdForDrawer
      : getSelectedScaleId;

    const [expandedDescriptions, setExpandedDescriptions] = useState<
      Record<string, boolean>
    >({});
    const [editingComment, setEditingComment] = useState<string | null>(null);
    const [editingCommentValue, setEditingCommentValue] = useState<string>('');
    const [isScaleModalOpen, setIsScaleModalOpen] = useState<boolean>(false);
    const [scalesToDisplay, setScalesToDisplay] = useState<any[]>([]);

    const competencyRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const {
      sentinelRef,
      stickyHeaderRef,
      isHeaderSticky,
      topOffset,
      zIndex,
      alertBarHeight,
    } = useStickyHeader({
      isDrawerMode,
    });

    const topOffsetWithAlertBar =
      topOffset + (isDrawerMode ? alertBarHeight : 0);

    useEffect(() => {
      const allCompetencies = filteredCompetencyGroups.flatMap(
        (g: any) => g?.competencies || []
      );
      const competencyIds = allCompetencies.map((comp: any) => String(comp?.id)
      );
      const firstErrorCompId = competencyIds.find(
        (compId: any) => validationErrorsByComponentId?.[compId]
      );

      if (firstErrorCompId) {
        const el = competencyRefs.current[firstErrorCompId];
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, [validationErrorsByComponentId, filteredCompetencyGroups]);

    const loggedUser = useAppSelector(loggedUserSelector.selected);

    // Memoize user IDs and states
    const { reviewerId, revieweeId, isReviewerViewingOwn } = useMemo(() => {
      const revId = String(evaluation?.reviewers?.[0]?.id || '');
      const reveeId = String(evaluation?.reviewee?.id || '');
      const isReviewer = String(loggedUser?.id || '') === revId;
      return {
        reviewerId: revId,
        revieweeId: reveeId,
        isReviewerViewingOwn: isReviewer,
      };
    }, [evaluation?.reviewers, evaluation?.reviewee?.id, loggedUser?.id]);

    // Memoize submission states
    const { selfSubmitted, managerSubmitted, isCompleted, employeeVisibility } =
      useMemo(() => {
        const selfSub =
          evaluation?.reviewee?.status === 'submitted' ||
          Boolean((evaluation as any)?.reviewee?.submitted_at);
        const managerSub = Array.isArray(evaluation?.reviewers)
          ? (evaluation?.reviewers as any[]).some(
              (r: any) => r?.status === 'submitted' || Boolean(r?.submitted_at)
            )
          : false;
        const completed = Boolean((evaluation as any)?.completed_at);
        const empVis = evaluation?.form?.employee_visibility;
        return {
          selfSubmitted: selfSub,
          managerSubmitted: managerSub,
          isCompleted: completed,
          employeeVisibility: empVis,
        };
      }, [
        evaluation?.reviewee?.status,
        evaluation?.reviewers,
        evaluation?.form?.employee_visibility,
      ]);

    const {
      overallCompetenciesAverageFilled,
      overallCompetenciesTotalBlocks,
      overallColor,
    } = useMemo(() => {
      const allCompetencies = filteredCompetencyGroups.flatMap(
        (g: any) => g?.competencies || []
      );
      if (!allCompetencies.length) {
        return {
          overallCompetenciesAverageFilled: 0,
          overallCompetenciesTotalBlocks: 4,
          overallColor: '#2563EB',
        };
      }

      const values: number[] = [];
      let maxLen = 2;

      allCompetencies.forEach((comp: any) => {
        const revieweeSelectedScaleId = effectiveGetSelectedScaleId(
          'reviewee',
          comp.id
        );
        const reviewerSelectedScaleId = effectiveGetSelectedScaleId(
          'reviewer',
          comp.id
        );
        const scales = getSortedScales(comp);

        const value = computeCompetencyDisplayValue(
          comp,
          isReviewerViewingOwn,
          managerSubmitted,
          revieweeSelectedScaleId,
          reviewerSelectedScaleId,
          selfSubmitted
        );

        if (value > 0) values.push(value);
        const len = scales.length;
        if (len > maxLen) maxLen = len;
      });

      const avg = values.length
        ? roundToOneDecimal(
            values.reduce((sum, v) => sum + v, 0) / values.length
          )
        : 0;
      const firstComp = allCompetencies[0];
      const scales = firstComp ? getSortedScales(firstComp) : [];
      const color = resolveScaleColorForValue(scales, avg, '#2563EB');

      return {
        overallCompetenciesAverageFilled: avg,
        overallCompetenciesTotalBlocks: maxLen,
        overallColor: color,
      };
    }, [
      filteredCompetencyGroups,
      effectiveGetSelectedScaleId,
      isReviewerViewingOwn,
      managerSubmitted,
      selfSubmitted,
    ]);

    // Event handlers
    const { handleScaleSelect, handleSaveComment: saveComment } =
      useCompetencyHandlers(isReviewerViewingOwn, reviewerId, revieweeId);

    const toggleDescription = useCallback((sectionId: string) => {
      setExpandedDescriptions((prev) => ({
        ...prev,
        [sectionId]: !prev[sectionId],
      }));
    }, []);

    const handleSaveComment = useCallback(
      (competencyId: string) => {
        if (isDrawerMode) return;
        try {
          saveComment(competencyId, editingCommentValue);
          setEditingComment(null);
          setEditingCommentValue('');
        } catch (_) {
          setEditingComment(null);
          setEditingCommentValue('');
        }
      },
      [saveComment, editingCommentValue, isDrawerMode]
    );

    const handleCancelComment = useCallback(() => {
      if (isDrawerMode) return;
      setEditingComment(null);
      setEditingCommentValue('');
    }, [isDrawerMode]);

    const handleEditComment = useCallback(
      (competencyId: string, currentComment: string) => {
        if (isDrawerMode) return;
        setEditingComment(competencyId);
        setEditingCommentValue(currentComment);
      },
      [isDrawerMode]
    );

    const handleShowRatingScales = useCallback(
      (scales: any[]) => {
        if (isDrawerMode) {
          if (onShowRatingScalesInDrawer) {
            onShowRatingScalesInDrawer(scales || []);
          }
          return;
        }
        setScalesToDisplay(scales || []);
        setIsScaleModalOpen(true);
      },
      [isDrawerMode, onShowRatingScalesInDrawer]
    );

    const handleSetCompetencyRef = useCallback(
      (competencyId: string, ref: HTMLDivElement | null) => {
        competencyRefs.current[competencyId] = ref;
      },
      []
    );

    return (
      <>
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div ref={sentinelRef} className="tw-h-0 tw-w-full tw-mb-[-16px]" />
          <div
            ref={stickyHeaderRef}
            style={{
              zIndex,
              ...(isHeaderSticky
                ? { top: `${topOffsetWithAlertBar}px`, position: 'sticky' }
                : {}),
            }}
            className={`${
              isHeaderSticky
                ? '!tw-bg-white !tw-p-5 !tw-border-b tw-border-0 tw-border-solid !tw-border-gray-border' +
                  ' tw-transition-all tw-duration-200'
                : ''
            }`}
          >
            <CompetencyHeader
              totalBlocks={overallCompetenciesTotalBlocks}
              averageFilled={overallCompetenciesAverageFilled}
              color={overallColor}
            />
          </div>

          {filteredCompetencyGroups.length > 0 ? (
            filteredCompetencyGroups.map((group: any) => (
              <CompetencyCategory
                key={group?.id}
                group={group}
                evaluation={evaluation}
                isReviewerViewingOwn={isReviewerViewingOwn}
                managerSubmitted={managerSubmitted}
                selfSubmitted={selfSubmitted}
                isCompleted={isCompleted}
                employeeVisibility={employeeVisibility}
                expandedDescriptions={expandedDescriptions}
                editingComment={editingComment}
                editingCommentValue={editingCommentValue}
                validationErrorsByComponentId={validationErrorsByComponentId}
                isDrawerMode={isDrawerMode}
                getSelectedScaleId={effectiveGetSelectedScaleId}
                responsesByComponentId={responsesByComponentId}
                onToggleDescription={toggleDescription}
                onShowRatingScales={handleShowRatingScales}
                onScaleSelect={handleScaleSelect}
                onEditComment={handleEditComment}
                onSaveComment={handleSaveComment}
                onCancelComment={handleCancelComment}
                onEditingValueChange={setEditingCommentValue}
                onSetRef={handleSetCompetencyRef}
              />
            ))
          ) : (
            <Card>
              <Card.Content className="tw-flex tw-flex-row !tw-p-5 tw-items-center tw-justify-center tw-gap-2">
                <Icon type="briefcase_job" className="tw-text-gray-textGray" />
                <div className="tw-text-base tw-text-gray-muted tw-font-medium">
                  {t('performance:empty_competencies_description')}
                </div>
              </Card.Content>
            </Card>
          )}
        </div>
        {isScaleModalOpen && !isDrawerMode && (
          <RatingScalesModal
            show
            onHide={() => {
              setIsScaleModalOpen(false);
              setScalesToDisplay([]);
            }}
            scales={scalesToDisplay as any}
          />
        )}
      </>
    );
  }
);

interface CompetencyCategoryProps {
  group: any;
  evaluation: any;
  isReviewerViewingOwn: boolean;
  managerSubmitted: boolean;
  selfSubmitted: boolean;
  isCompleted: boolean;
  employeeVisibility: boolean;
  expandedDescriptions: Record<string, boolean>;
  editingComment: string | null;
  editingCommentValue: string;
  validationErrorsByComponentId: Record<string, string>;
  isDrawerMode: boolean;
  getSelectedScaleId: (
    role: 'reviewee' | 'reviewer',
    competencyId: string
  ) => string | undefined;
  responsesByComponentId: Record<
    string,
    { answersByUserId: Record<string, any> }
  >;
  onToggleDescription: (competencyId: string) => void;
  onShowRatingScales: (scales: any[]) => void;
  onScaleSelect: (
    role: 'reviewee' | 'reviewer',
    competencyId: string,
    scaleId: string
  ) => void;
  onEditComment: (competencyId: string, comment: string) => void;
  onSaveComment: (competencyId: string) => void;
  onCancelComment: () => void;
  onEditingValueChange: (value: string) => void;
  onSetRef: (competencyId: string, ref: HTMLDivElement | null) => void;
}

const CompetencyCategory = memo<CompetencyCategoryProps>(
  ({
    group,
    evaluation,
    isReviewerViewingOwn,
    managerSubmitted,
    selfSubmitted,
    isCompleted,
    employeeVisibility,
    expandedDescriptions,
    editingComment,
    editingCommentValue,
    validationErrorsByComponentId,
    isDrawerMode,
    getSelectedScaleId,
    responsesByComponentId,
    onToggleDescription,
    onShowRatingScales,
    onScaleSelect,
    onEditComment,
    onSaveComment,
    onCancelComment,
    onEditingValueChange,
    onSetRef,
  }) => {
    // Memoize category calculations
    const { categoryAvg, categoryTotalBlocks, categoryColor } = useMemo(() => {
      const competenciesInCategory = group?.competencies || [];
      const categoryValues: number[] = [];
      let maxLen = 2;

      competenciesInCategory.forEach((comp: any) => {
        const revieweeSelectedScaleId = getSelectedScaleId('reviewee', comp.id);
        const reviewerSelectedScaleId = getSelectedScaleId('reviewer', comp.id);
        const scales = getSortedScales(comp);

        const value = computeCompetencyDisplayValue(
          comp,
          isReviewerViewingOwn,
          managerSubmitted,
          revieweeSelectedScaleId,
          reviewerSelectedScaleId,
          selfSubmitted
        );

        if (value > 0) categoryValues.push(value);
        const len = scales.length;
        if (len > maxLen) maxLen = len;
      });

      const avg = categoryValues.length
        ? roundToOneDecimal(
            categoryValues.reduce((s, v) => s + v, 0) / categoryValues.length
          )
        : 0;

      const firstCompInCategory = competenciesInCategory?.[0];
      const catScales = firstCompInCategory
        ? getSortedScales(firstCompInCategory)
        : [];
      const color = resolveScaleColorForValue(catScales, avg, '#2563EB');

      return {
        categoryAvg: avg,
        categoryTotalBlocks: maxLen,
        categoryColor: color,
      };
    }, [
      group?.competencies,
      getSelectedScaleId,
      isReviewerViewingOwn,
      managerSubmitted,
      selfSubmitted,
    ]);

    const handleShowRatingScales = useCallback(() => {
      onShowRatingScales(evaluation?.rating_scales || []);
    }, [onShowRatingScales, evaluation?.rating_scales]);

    return (
      <Card>
        <Card.Content className="tw-flex tw-flex-col !tw-p-5 !tw-gap-5">
          <CategoryHeader
            categoryName={group?.name}
            totalBlocks={categoryTotalBlocks}
            averageFilled={categoryAvg}
            color={categoryColor}
            onShowRatingScales={handleShowRatingScales}
          />

          {(group?.competencies || []).map((comp: any) => (
            <CompetencyItemWrapper
              key={comp.id}
              competency={comp}
              evaluation={evaluation}
              isReviewerViewingOwn={isReviewerViewingOwn}
              managerSubmitted={managerSubmitted}
              selfSubmitted={selfSubmitted}
              isCompleted={isCompleted}
              employeeVisibility={employeeVisibility}
              expandedDescriptions={expandedDescriptions}
              editingComment={editingComment}
              editingCommentValue={editingCommentValue}
              validationError={validationErrorsByComponentId[String(comp.id)]}
              isDrawerMode={isDrawerMode}
              responsesByComponentId={responsesByComponentId}
              onToggleDescription={onToggleDescription}
              onShowRatingScales={onShowRatingScales}
              onScaleSelect={onScaleSelect}
              onEditComment={onEditComment}
              onSaveComment={onSaveComment}
              onCancelComment={onCancelComment}
              onEditingValueChange={onEditingValueChange}
              onSetRef={onSetRef}
            />
          ))}
        </Card.Content>
      </Card>
    );
  }
);

// Wrapper component to handle useCompetencyData hook properly
interface CompetencyItemWrapperProps {
  competency: any;
  evaluation: any;
  isReviewerViewingOwn: boolean;
  managerSubmitted: boolean;
  selfSubmitted: boolean;
  isCompleted: boolean;
  employeeVisibility: boolean;
  expandedDescriptions: Record<string, boolean>;
  editingComment: string | null;
  editingCommentValue: string;
  validationError?: string;
  isDrawerMode: boolean;
  responsesByComponentId: Record<
    string,
    { answersByUserId: Record<string, any> }
  >;
  onToggleDescription: (competencyId: string) => void;
  onShowRatingScales: (scales: any[]) => void;
  onScaleSelect: (
    role: 'reviewee' | 'reviewer',
    competencyId: string,
    scaleId: string
  ) => void;
  onEditComment: (competencyId: string, comment: string) => void;
  onSaveComment: (competencyId: string) => void;
  onCancelComment: () => void;
  onEditingValueChange: (value: string) => void;
  onSetRef: (competencyId: string, ref: HTMLDivElement | null) => void;
}

const CompetencyItemWrapper = memo<CompetencyItemWrapperProps>(
  ({
    competency,
    evaluation,
    isReviewerViewingOwn,
    managerSubmitted,
    selfSubmitted,
    isCompleted,
    employeeVisibility,
    expandedDescriptions,
    editingComment,
    editingCommentValue,
    validationError,
    isDrawerMode,
    responsesByComponentId,
    onToggleDescription,
    onShowRatingScales,
    onScaleSelect,
    onEditComment,
    onSaveComment,
    onCancelComment,
    onEditingValueChange,
    onSetRef,
  }) => {
    const { getSelectedScaleId } = useEvaluationStore();

    const competencyData = React.useMemo(() => {
      const competencyId = String(competency.id);
      const reviewerId = String(evaluation?.reviewers?.[0]?.id || '');
      const revieweeId = String(evaluation?.reviewee?.id || '');
      const entry = responsesByComponentId?.[competencyId];

      // Get scale selections from responses
      const revieweeRaw = revieweeId
        ? entry?.answersByUserId?.[revieweeId]
        : undefined;
      const revieweeAnswerObj: any =
        revieweeRaw && typeof revieweeRaw === 'object'
          ? (revieweeRaw as any).answer || revieweeRaw
          : undefined;
      const revieweeSelectedScaleId =
        revieweeAnswerObj?.scale_id ||
        revieweeAnswerObj?.scaleId ||
        (!isDrawerMode
          ? getSelectedScaleId('reviewee', competencyId)
          : undefined);
      const revieweeComment: string = revieweeAnswerObj?.comment || '';

      const reviewerRaw = reviewerId
        ? entry?.answersByUserId?.[reviewerId]
        : undefined;
      const reviewerAnswerObj: any =
        reviewerRaw && typeof reviewerRaw === 'object'
          ? (reviewerRaw as any).answer || reviewerRaw
          : undefined;
      const reviewerSelectedScaleId =
        reviewerAnswerObj?.scale_id ||
        reviewerAnswerObj?.scaleId ||
        (!isDrawerMode
          ? getSelectedScaleId('reviewer', competencyId)
          : undefined);
      const reviewerComment: string = reviewerAnswerObj?.comment || '';

      return {
        revieweeSelectedScaleId,
        reviewerSelectedScaleId,
        revieweeComment,
        reviewerComment,
      };
    }, [
      competency.id,
      evaluation,
      responsesByComponentId,
      isDrawerMode,
      getSelectedScaleId,
    ]);

    return (
      <CompetencyItem
        competency={competency}
        evaluation={evaluation}
        isReviewerViewingOwn={isReviewerViewingOwn}
        managerSubmitted={managerSubmitted}
        selfSubmitted={selfSubmitted}
        isCompleted={isCompleted}
        employeeVisibility={employeeVisibility}
        expandedDescriptions={expandedDescriptions}
        editingComment={editingComment}
        editingCommentValue={editingCommentValue}
        validationError={validationError}
        revieweeSelectedScaleId={competencyData.revieweeSelectedScaleId}
        reviewerSelectedScaleId={competencyData.reviewerSelectedScaleId}
        revieweeComment={competencyData.revieweeComment}
        reviewerComment={competencyData.reviewerComment}
        isDrawerMode={isDrawerMode}
        onToggleDescription={onToggleDescription}
        onShowRatingScales={onShowRatingScales}
        onScaleSelect={onScaleSelect}
        onEditComment={onEditComment}
        onSaveComment={onSaveComment}
        onCancelComment={onCancelComment}
        onEditingValueChange={onEditingValueChange}
        onSetRef={onSetRef}
      />
    );
  }
);

CompetencyContainerReadOnly.displayName = 'CompetencyContainerReadOnly';
CompetencyCategory.displayName = 'CompetencyCategoryReadOnly';
CompetencyItemWrapper.displayName = 'CompetencyItemWrapperReadOnly';

export default CompetencyContainerReadOnly;
